import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import {
  AppModelModalComponent,
  AuthService,
  Column,
  CustomAction,
  DataTableComponent,
  NimmstaDataSource,
  OrganizationService,
  StatusBadgePipe,
  User,
  UserRolesPipe,
  UserStatus,
  NimmstaCommonsModuleConfiguration,
  launchImpersonationInNewTab,
  IconAction,
  ErrorComponent,
  EmptyTableComponent,
  DateTimePipe,
} from '@nimmsta/ng-commons-lib';
import { UserFormComponent } from '../user/user-form.component';
import { OrganizationUserService } from '../../services/organization-user.service';
import { ImpersonateAction } from '../user/impersonateAction';
import { DeleteUserAction } from '../user/deleteUserAction';
import { LockUnlockAction } from '../user/lockUnlockAction';
import { CheckSecondaryUserAction } from '../user/checkSecondaryUserAction';

@Component({
  selector: 'app-organization-user',
  templateUrl: 'organization-user.component.html',
  imports: [
    UserFormComponent,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    ErrorComponent,
  ],
  providers: [StatusBadgePipe, DateTimePipe, UserRolesPipe],
})
export class OrganizationUserComponent implements OnInit {
  private orgService = inject(OrganizationService);
  private userService = inject(OrganizationUserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private statusBadge = inject(StatusBadgePipe);
  private configuration = inject(NimmstaCommonsModuleConfiguration);
  private dateTimePipe = inject(DateTimePipe);
  private userRolesPipe = inject(UserRolesPipe);

  userSource!: NimmstaDataSource<User>;

  title?: string;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<User>;

  @ViewChild('EditModal', { static: false })
  private editModalComponent!: AppModelModalComponent<User>;

  @ViewChild(UserFormComponent, { static: true })
  private userFormComponent!: UserFormComponent;

  customActions: CustomAction<User>[] = [];

  iconActions: IconAction<User>[] = [];

  private orgId?: string;

  error?: HttpErrorResponse;

  @Input() showNewAction = true;

  get columns(): Column[] {
    return [
      new Column('family_name', 'user.family_name', { sortable: true }),
      new Column('given_name', 'user.given_name', { sortable: true }),
      new Column('email', 'user.email', { sortable: true }),
      new Column('roles', 'user.roles', { pipe: this.userRolesPipe }),
      new Column('status', 'user.status', {
        sortable: true,
        pipe: this.statusBadge,
      }),
      new Column('createdAt', 'user.createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  ngOnInit(): void {
    const orgId = this.route.snapshot.paramMap.get('id');
    if (!orgId) {
      throw new Error('Organization id is required');
    }
    this.orgId = orgId;
    this.orgService.get(this.orgId).subscribe((org) => {
      this.title = org.name;
    });
    this.userService.setOrgId(this.orgId);
    this.userSource = new NimmstaDataSource<User>(this.userService);
    if (this.configuration.applicationUrls.workflow) {
      const impersonateAction = new ImpersonateAction(
        'impersonate',
        this.authService,
        'cloud',
      );
      impersonateAction.subscribe((id) =>
        this.impersonate(
          id,
          new URL(this.configuration.applicationUrls.workflow!),
        ),
      );
      this.iconActions.push(impersonateAction);
    }
    const editAction = new CheckSecondaryUserAction('edit', false);
    editAction.subscribe((id) => this.edit(id));
    this.customActions.push(editAction);
    const deleteAction = new DeleteUserAction('delete', this.authService);
    deleteAction.subscribe((id) => this.delete(id));
    this.customActions.push(deleteAction);
    const removeAction = new CheckSecondaryUserAction(
      'user.secondary-user.remove',
      true,
    );
    removeAction.subscribe((id) => this.removeSecondaryOrg(id));
    this.customActions.push(removeAction);
    const lockUnlockAction = new LockUnlockAction('user.unlock');
    this.customActions.push(lockUnlockAction);
    lockUnlockAction.subscribe((id) => this.toggleLock(id));
  }

  new(): void {
    this.translate.get('user.new').subscribe((value) => {
      const user = {} as User;
      user.organizationId = this.orgId;
      this.openModal(value, true, user);
    });
  }

  impersonate(id: string, target: URL): void {
    this.userSource.data?.content
      .filter((d) => id === d.id)
      .forEach((user) => {
        this.authService.impersonate(user.email).subscribe({
          next: (data) => {
            const error = launchImpersonationInNewTab(
              this.authService,
              data,
              target,
            );
            if (error) {
              this.error = new HttpErrorResponse({
                error: { error: error.message },
              });
            }
          },
          error: (error) => {
            this.error = new HttpErrorResponse({
              ...error,
              error: { ...error.error, error: `httperror.${error.status}` },
            });
          },
        });
      });
  }

  edit(id: string): void {
    this.translate.get('user.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, user: User): void {
    this.userFormComponent.editable = editable;
    this.userFormComponent.setValue(user);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show('sm');
  }

  delete(id: string): void {
    this.error = undefined;
    this.userService.delete(this.getSelectedObject(id)!).subscribe({
      next: () => this.tableComponent.loadDataPage(),
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  removeSecondaryOrg(id: string): void {
    this.userService.removeSecondaryOrg(id).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  toggleLock(id: string): void {
    const user = this.getSelectedObject(id)!;
    if (user.status === UserStatus.ACTIVE) {
      this.lock(id);
    } else {
      this.unlock(id);
    }
  }

  lock(id: string): void {
    lastValueFrom(this.userService.lock(this.getSelectedObject(id)!)).then(() =>
      this.tableComponent.loadDataPage(),
    );
  }

  unlock(id: string): void {
    lastValueFrom(this.userService.unlock(this.getSelectedObject(id)!)).then(
      () => this.tableComponent.loadDataPage(),
    );
  }

  onSave(): void {
    this.userFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): User | undefined {
    return this.userSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
